import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table
} from "reactstrap";

import { useTheme } from "../hooks/useTheme";
import { useSortData } from "../hooks/useSortData";
import { useSearchData } from "../hooks/useSearchData";
import { useFilterDataByField } from "../hooks/useFilterDataByField";

import DataLoaderFailed from "./loader/DataLoaderFailed";
import DataLoader from "./loader/DataLoader";
import SortIcon from "./SortIcon";
import DataFilter from "./DataFilter";
import Download from "./Download";

// this component does not query the database by page number - it will fetch all database records that meet the filter criteria.
// it should be used for queries returning only a few records
const DataTable = ({
  columns,
  data,
  queryForDownload,
  isLoading,
  error,
  searchableFields = [],
  paging,
  onRowClick,
  filters = [],
  actions = [],
  exportToCsv = false,
  csvFileName = "data.csv",
  sortByColumn,
  sortDirection = "ascending",
  onFilterChange,
  getRowClassName,
  getCellClassName,
  onFilteredDataChange,
}) => {
  const theme = useTheme();

  // Advanced filters state and animations
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const mountedStyle = { animation: "inAnimation 250ms ease-in" };
  const unmountedStyle = { animation: "outAnimation 270ms ease-out", animationFillMode: "forwards" };

  // Search and sorting hooks
  const initialSort = useMemo(() => {
    const key = sortByColumn || columns[0]?.key || "";
    const direction = sortDirection || "ascending";
    return { key, direction };
  }, [sortByColumn, sortDirection, columns]);

  const { sortedData, sortBy, sortConfig } = useSortData(
    Array.isArray(data) ? data : [],
    initialSort
  );

  const [searchFilteredData, setSearch] = useSearchData(sortedData, searchableFields);
  const [fieldFilteredData, setFilter] = useFilterDataByField(searchFilteredData);
  const [filterValues, setFilterValues] = useState(
    filters.map((x) => ({ id: x.id, value: x.defaultValue, customIncludeFunction: x.customIncludeFunction }))
  );

  // Dynamic paging state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const numPerPageOptions = [10, 20, 50, 200];

  // if (!Array.isArray(data)) {
  //   console.error("Datatable: Passed in data was not an array", columns);
  // }

  // Reset to first page when filter values change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues]);

  // ----- Compute filtered and paginated data BEFORE any effects that read them -----

  const customFilteredData = useMemo(() => {
    const customFilters = filterValues.filter((x) => typeof x.customIncludeFunction === "function");
    return fieldFilteredData.filter((dataItem) =>
      customFilters.every((filter) => filter.customIncludeFunction(dataItem, filter.value))
    );
  }, [fieldFilteredData, filterValues]);

  // Calculate total pages with guard
  const safePageSize = pageSize > 0 ? pageSize : 10;
  const totalPages = Math.max(0, Math.ceil(customFilteredData.length / safePageSize));

  // Determine data to display: slice the filteredData if paging is enabled
  const pagedData = paging
    ? customFilteredData.slice((currentPage - 1) * safePageSize, currentPage * safePageSize)
    : customFilteredData;

  // Clamp currentPage if totalPages shrinks (must run AFTER totalPages is defined)
  useEffect(() => {
    if (!paging) return;
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [paging, totalPages, currentPage]);

  // Bubble up the filtered (but unpaged) rows — guard identity to avoid loops
  const lastCustomFilteredRef = useRef(customFilteredData);
  useEffect(() => {
    if (typeof onFilteredDataChange !== "function") return;
    const prev = lastCustomFilteredRef.current;
    // cheap equality: same ref or same length
    if (prev === customFilteredData || prev.length === customFilteredData.length) {
      lastCustomFilteredRef.current = customFilteredData;
      return;
    }
    lastCustomFilteredRef.current = customFilteredData;
    onFilteredDataChange(customFilteredData);
  }, [customFilteredData, onFilteredDataChange]);

  // Handler for filter changes
  const handleFilterChange = useCallback(
    (filter, value) => {
      if (value === undefined || value === null) {
        setFilter(filter.id, "");
      } else if (!filter.customIncludeFunction) {
        setFilter(filter.id, value);
      }
      setFilterValues((prev) =>
        prev.map((x) =>
          x.id === filter.id ? { ...x, value, customIncludeFunction: x.customIncludeFunction } : x
        )
      );
      if (onFilterChange) onFilterChange({ filterId: filter.id, value });
    },
    [onFilterChange, setFilter]
  );

  // Handler for search input change
  const onSearchChanged = useCallback(
    (e) => {
      setSearch(e.target.value);
    },
    [setSearch]
  );

  // Handler for items per page change
  const handlePageSizeChange = useCallback((e) => {
    const next = Math.max(1, Number(e.target.value) || 10);
    setPageSize(next);
    setCurrentPage(1);
  }, []);

  if (error) return <DataLoaderFailed />;
  if (isLoading) return <DataLoader />;

  const hasToolbar =
    searchableFields.length > 0 ||
    filters.length > 0 ||
    actions.length > 0 ||
    exportToCsv;

  return (
    <>
      <Container fluid className="px-0">
        {hasToolbar && (
        <div
          className="d-flex justify-content-between align-items-center flex-wrap gap-3"
          style={{ minHeight: "40px" }}
        >
          {/* Search Bar */}
          {searchableFields.length > 0 && (
            <Form style={{ flex: "1 1 auto", minWidth: "250px" }} onSubmit={(e) => e.preventDefault()}>
              <Label className="visually-hidden" htmlFor="search">
                Search
              </Label>
              <Input
                type="search"
                id="search"
                placeholder={`Search ${customFilteredData.length} records`}
                onChange={onSearchChanged}
                style={{ height: "38px" }}
              />
            </Form>
          )}

          <div className="d-flex gap-2 flex-wrap align-items-center">
            {filters
              .filter((filter) => !filter.advanced)
              .map((filter, index) => {
                return (
                  <DataFilter
                    key={index}
                    id={filter.id}
                    label={filter.label}
                    type={filter.type}
                    placeholder={filter.placeholder}
                    isSearchable={filter.isSearchable}
                    options={filter.options}
                    value={
                      filter.type !== "select"
                        ? filter.value
                        : filter.options.find((opt) => opt.value === filter.value) || null
                    }
                    onChange={(value) => handleFilterChange(filter, value)}
                    isInvalid={filter.isInvalid}
                    defaultValue={filter.defaultValue}
                  />
                );
              })}
          </div>

          {/* Advanced Filters Toggle */}
          {filters.filter((filter) => filter.advanced).length > 0 && (
            <Button
              outline={!advancedFiltersOpen}
              color={theme === "dark-mode" ? "dark" : "light"}
              onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
            >
              <i className="bi bi-funnel"></i>
            </Button>
          )}

          <div style={{ flexGrow: 1 }}></div>

          {/* Action Buttons */}
          <div className="d-flex gap-2">
            {actions.map((action, index) => (
              <div key={index}>{action}</div>
            ))}
          </div>
          {exportToCsv && (
            <Download
              fileName={csvFileName}
              data={customFilteredData}
              showIcon={false}
              alternativeQueryFunction={queryForDownload}
              text="Export"
            />
          )}
        </div>
        )}
      </Container>

      {/* Advanced Filters */}
      {advancedFiltersOpen && (
        <>
          <br />
          <Row>
            <Container
              fluid
              style={advancedFiltersOpen ? mountedStyle : unmountedStyle}
              className="contrast-dropdown py-3 transitionDiv"
            >
              <div style={{ minHeight: "40px" }}>
                {filters
                  .filter((filter) => filter.advanced)
                  .map((filter, index) => {
                    return (
                      <DataFilter
                        key={index}
                        id={filter.id}
                        label={filter.label}
                        type={filter.type}
                        placeholder={filter.placeholder}
                        isSearchable={filter.isSearchable}
                        options={filter.options}
                        value={
                          filter.type !== "select"
                            ? filter.value
                            : filter.options.find((opt) => opt.value === filter.value) || null
                        }
                        onChange={(value) => handleFilterChange(filter, value)}
                        isInvalid={filter.isInvalid}
                        defaultValue={filter.defaultValue}
                        advanced={true}
                      />
                    );
                  })}
              </div>
            </Container>
          </Row>
        </>
      )}

      <Container fluid className="px-0">
        <Table
          className={theme === "dark-mode" ? "no-wrap mt-3 align-middle table-dark" : "no-wrap mt-3 align-middle"}
          hover
          responsive
          striped
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ cursor: "pointer", textAlign: col.align === "right" ? "right" : "left", ...(col.style || {}) }}
                  onClick={() => !col.isSortDisabled && sortBy(col.key)}
                >
                  <span style={{ display: "flex" }}>
                    {col.label}
                    &nbsp;
                    {sortConfig?.key === col.key && !col.isSortDisabled && <SortIcon sort={sortConfig?.direction} />}
                    {col.isSortDisabled && <i style={{ opacity: 0.2 }} className="bi bi-chevron-expand"></i>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedData.map((item, index) => (
              <tr
                key={item.id ?? item.Id ?? index}
                className={[
                  onRowClick ? "cursor-pointer" : "",
                  getRowClassName ? getRowClassName(item) : ""
                ]
                  .join(" ")
                  .trim()}
                onClick={(e) => {
                  if (e.target.closest("button, i")) return;
                  if (onRowClick) onRowClick(item, index, e);
                }}
                style={onRowClick ? { cursor: "pointer" } : {}}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[col.className || "", getCellClassName ? getCellClassName(item, col.key) : ""]
                      .join(" ")
                      .trim()}
                    style={{textAlign: col.align === "right" ? "right" : "left"}}
                  >
                    {col.render ? col.render(item[col.key], item, index) : item[col.key]}
                  </td>
                ))}
              </tr>
            ))}
            {Array.isArray(pagedData) && pagedData.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Pagination Controls */}
        {paging && (
          <Row className="mt-3 unlocked">
            <Col className="d-flex justify-content-start">
              <Pagination aria-label="Page navigation">
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink
                    first
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink
                    previous
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(currentPage - 1);
                    }}
                  />
                </PaginationItem>
                {(() => {
                  const pages = [];

                  const createPage = (pageNum) => (
                    <PaginationItem key={pageNum} active={currentPage === pageNum}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(pageNum);
                        }}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );

                  const lastPage = totalPages;

                  if (lastPage <= 4) {
                    // Just show all pages
                    for (let i = 1; i <= lastPage; i++) {
                      pages.push(createPage(i));
                    }
                  } else {
                    let startPage = currentPage;

                    if (startPage > lastPage - 3) {
                      // Clamp to last 4 pages
                      startPage = lastPage - 3;
                    }

                    const visiblePages = [];
                    for (let i = startPage; i < startPage + 3; i++) {
                      visiblePages.push(i);
                    }

                    visiblePages.forEach((page) => pages.push(createPage(page)));

                    if (startPage + 3 <= lastPage) {
                      // Show final page or ellipsis
                      if (startPage + 3 === lastPage) {
                        pages.push(createPage(lastPage));
                      } else {
                        pages.push(
                          <PaginationItem key="ellipsis" disabled>
                            <PaginationLink href="#">...</PaginationLink>
                          </PaginationItem>
                        );
                      }
                    }
                  }

                  return pages;
                })()}
                <PaginationItem disabled={currentPage === totalPages || totalPages === 0}>
                  <PaginationLink
                    next
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(currentPage + 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem disabled={currentPage === totalPages || totalPages === 0}>
                  <PaginationLink
                    last
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(totalPages);
                    }}
                  />
                </PaginationItem>
              </Pagination>
              <div className="ps-3 p-2 d-flex justify-content-end">
                <span className="text-muted">
                  {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "No records found"}
                </span>
              </div>
            </Col>
            <Col className="d-flex justify-content-end">
              <Input
                id="pageSizeSelect"
                name="select"
                type="select"
                value={pageSize}
                style={{ width: "5rem", height: "3rem" }}
                bsSize="sm"
                onChange={handlePageSizeChange}
              >
                {numPerPageOptions.map((num, index) => (
                  <option key={index} value={num}>
                    {num}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default DataTable;
